import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { Check, ChevronDown } from 'lucide-react'
import type { ViewControl, ViewOption } from 'lib/panelViewControls'
import styles from './view-pill.module.scss'

// fixed_header: a list's tab or sort order in the panel's top bar, as a pill
// with a caret: it switches the view in place (it leads nowhere, unlike a
// breadcrumb's chevrons), and its dropdown lists the other choices.

function OptionIcon({ icon }: { icon: ViewOption['icon'] }) {
    if (!icon) return null
    if (typeof icon === 'string')
        return <i className={cn(icon, styles.faIcon)} aria-hidden="true" />
    const Icon = icon as React.ComponentType<{ size?: number; className?: string }>
    return <Icon size={14} className={styles.icon} />
}

export default function ViewPill({
    control,
    accent = false,
    asTitle = false,
    className,
    ariaLabel,
}: {
    control: ViewControl
    // The tab pill wears the accent; the sort pill stays neutral.
    accent?: boolean
    // As the panel's name itself (Home's Public / Following): title type, a
    // caret, no pill.
    asTitle?: boolean
    className?: string
    ariaLabel: string
}) {
    const ref = useRef<HTMLButtonElement>(null)
    const [menu, setMenu] = useState<{ top: number; left: number } | null>(null)
    const active = control.options.find(o => o.key === control.active) || control.options[0]
    if (!active) return null

    const open = (ev: React.MouseEvent) => {
        ev.stopPropagation()
        const rect = ref.current?.getBoundingClientRect()
        if (!rect) return
        const width = 200
        setMenu({
            top: rect.bottom + 6,
            left: Math.max(12, Math.min(rect.left, window.innerWidth - 12 - width)),
        })
    }

    return (
        <>
            <button
                ref={ref}
                type="button"
                className={cn(
                    asTitle ? styles.titleSwitch : styles.pill,
                    !asTitle && accent && styles.accent,
                    className,
                )}
                onClick={open}
                aria-haspopup="menu"
                aria-expanded={Boolean(menu)}
                aria-label={`${ariaLabel}: ${active.label}`}
            >
                {!asTitle && <OptionIcon icon={active.icon} />}
                <span className={styles.label}>{active.label}</span>
                <ChevronDown size={13} strokeWidth={2.4} className={styles.caret} />
            </button>
            {menu &&
                createPortal(
                    <div
                        className={styles.backdrop}
                        onClick={ev => {
                            ev.stopPropagation()
                            setMenu(null)
                        }}
                    >
                        <div
                            className={styles.menu}
                            role="menu"
                            style={{ top: menu.top, left: menu.left }}
                            onClick={ev => ev.stopPropagation()}
                        >
                            {control.options.map(option => {
                                const selected = option.key === active.key
                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={selected}
                                        className={cn(styles.item, selected && styles.selected)}
                                        onClick={() => {
                                            setMenu(null)
                                            if (!selected) control.select(option.key)
                                        }}
                                    >
                                        <span className={styles.itemIcon}>
                                            <OptionIcon icon={option.icon} />
                                        </span>
                                        <span className={styles.itemLabel}>{option.label}</span>
                                        {selected && <Check size={14} className={styles.check} />}
                                    </button>
                                )
                            })}
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    )
}
